import React, { useState, useEffect } from "react";
import { useRegistration, useRegistrationDispatch } from "../context/RegistrationContext";
import Input from "../components/util/Input";
import Select from "../components/util/Select";
import Checkbox from "../components/util/Checkbox";

const DEFAULT_OPT    = { key: 0, value: "Select an Option" };
const DEFAULT_COUNTRY = { id: 0, code: "", countryName: "Select a Location" };
const DEFAULT_STATE   = { id: 0, code: "", stateName: "Select an Option" };

// First argument that is neither null nor undefined (stands in for `??`, unsupported by react-scripts 2.x)
function firstDefined() {
  for (var n = 0; n < arguments.length; n++) {
    if (arguments[n] !== null && arguments[n] !== undefined) return arguments[n];
  }
  return undefined;
}

function buildOptions(list, labelKey, valueKey = "key") {
  if (!list) return [DEFAULT_OPT];
  return [DEFAULT_OPT, ...list.map((i) => ({ key: firstDefined(i[valueKey], i.id, i.key), value: firstDefined(i[labelKey], i.value) }))];
}

export default function StepRegistrationForm() {
  const state    = useRegistration();
  const dispatch = useRegistrationDispatch();

  const { form, masterData, userProfile } = state;

  // Returning users start in read-only mode; new users go straight to edit mode
  const isReturning = !!userProfile && userProfile.exists === true;
  const [editMode, setEditMode] = useState(!isReturning);

  // States filtered by selected country
  const [filteredStates, setFilteredStates] = useState([DEFAULT_STATE]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!masterData || !form.countryId) {
      setFilteredStates([DEFAULT_STATE]);
      return;
    }
    const states = (masterData.states || []).filter(
      (s) => s.countryId === form.countryId || s.country_Id === form.countryId
    );
    setFilteredStates([DEFAULT_STATE, ...states]);
  }, [form.countryId, masterData]);

  const update = (field) => (e) => {
    const value = e && e.target !== undefined ? e.target.value : e;
    dispatch({ type: "UPDATE_FORM", payload: { [field]: value } });
  };

  const updateInt = (field) => (e) => {
    const raw = e && e.target !== undefined ? e.target.value : e;
    dispatch({ type: "UPDATE_FORM", payload: { [field]: parseInt(raw, 10) || 0 } });
  };

  const validate = () => {
    const errs = {};
    if (!(form.firstName || "").trim())    errs.firstName    = "Required";
    if (!(form.lastName || "").trim())     errs.lastName     = "Required";
    if (!(form.email || "").trim())        errs.email        = "Required";
    if (!form.jobRoleId)            errs.jobRoleId    = "Required";
    if (!(form.companyName || "").trim())  errs.companyName  = "Required";
    if (!form.countryId)            errs.countryId    = "Required";
    if (!(form.addressLine1 || "").trim()) errs.addressLine1 = "Required";
    if (!(form.city || "").trim())         errs.city         = "Required";
    if (!(form.zipCode || "").trim())      errs.zipCode      = "Required";
    if (!form.industryId)           errs.industryId   = "Required";
    if (!form.agreeToRegisterTerms) errs.agreeToRegisterTerms = "You must agree to the terms";
    return errs;
  };

  const handleContinue = () => {
    if (!editMode) {
      // Returning user confirmed read-only view — go next
      dispatch({ type: "NEXT_STEP" });
      return;
    }
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    dispatch({ type: "NEXT_STEP" });
  };

  const handleBack = () => {
    dispatch({ type: "PREV_STEP" });
  };

  // ── Master data option lists ─────────────────────────────────────────────
  const countryOptions  = masterData ? [DEFAULT_COUNTRY, ...(masterData.countries || [])] : [DEFAULT_COUNTRY];
  const jobRoleOptions  = buildOptions((masterData && masterData.jobRoles), "value");
  const industryOptions = buildOptions((masterData && masterData.industry), "value");
  const claimOptions    = buildOptions((masterData && masterData.claimTypes), "value");

  const title = isReturning ? "Verify Enterprise Account Information" : "Enterprise Account Registration";

  return (
    <div className="ucj-form-wrapper">
      <div className="ucj-form-card">
        {/* ── Header ── */}
        <div className="ucj-form-header">
          <img src={require("../images/logo.png")} alt="NVIDIA" className="ucj-logo" />
          <h2 className="ucj-form-title">{title}</h2>
          {state.pakId && (
            <p className="ucj-form-subtitle">
              PAK ID: {state.pakId}
            </p>
          )}
        </div>

        {/* ── Read-only banner for returning users ── */}
        {isReturning && !editMode && (
          <div className="ucj-returning-banner">
            We found your previous registration. Review your details below.
          </div>
        )}

        {/* ── Contact Details ── */}
        <section className="ucj-form-section">
          <h3 className="ucj-section-heading">Contact Details</h3>
          <div className="ucj-form-row">
            <Input
              label="First Name *"
              value={form.firstName}
              onChange={update("firstName")}
              disabled={!editMode}
              error={errors.firstName}
            />
            <Input
              label="Last Name *"
              value={form.lastName}
              onChange={update("lastName")}
              disabled={!editMode}
              error={errors.lastName}
            />
          </div>
          <Input
            label="Email Address *"
            value={form.email}
            onChange={update("email")}
            disabled={true}
            error={errors.email}
          />
          <Select
            label="Job Role *"
            value={form.jobRoleId}
            onChange={updateInt("jobRoleId")}
            options={jobRoleOptions}
            disabled={!editMode}
            error={errors.jobRoleId}
          />
        </section>

        {/* ── Company Address ── */}
        <section className="ucj-form-section">
          <h3 className="ucj-section-heading">Company Address</h3>
          <Input
            label="Company Name *"
            value={form.companyName}
            onChange={update("companyName")}
            disabled={!editMode}
            error={errors.companyName}
          />
          <div className="ucj-form-field">
            <label className="ucj-label">Country/Region *</label>
            <select
              className={`ucj-select${errors.countryId ? " ucj-select--error" : ""}`}
              value={form.countryId}
              disabled={!editMode}
              onChange={(e) => {
                dispatch({ type: "UPDATE_FORM", payload: { countryId: parseInt(e.target.value, 10) || 0, stateId: null } });
              }}
            >
              {countryOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.countryName}</option>
              ))}
            </select>
            {errors.countryId && <span className="ucj-error">{errors.countryId}</span>}
          </div>
          <div className="ucj-form-row">
            <Input
              label="Address Line 1 *"
              value={form.addressLine1}
              onChange={update("addressLine1")}
              disabled={!editMode}
              error={errors.addressLine1}
            />
            <Input
              label="Address Line 2"
              value={form.addressLine2}
              onChange={update("addressLine2")}
              disabled={!editMode}
            />
          </div>
          <div className="ucj-form-row ucj-form-row--three">
            <Input
              label="City *"
              value={form.city}
              onChange={update("city")}
              disabled={!editMode}
              error={errors.city}
            />
            <div className="ucj-form-field">
              <label className="ucj-label">State/Province</label>
              <select
                className="ucj-select"
                value={form.stateId || 0}
                disabled={!editMode}
                onChange={(e) => dispatch({ type: "UPDATE_FORM", payload: { stateId: parseInt(e.target.value, 10) || null } })}
              >
                {filteredStates.map((s) => (
                  <option key={s.id} value={s.id}>{s.stateName}</option>
                ))}
              </select>
            </div>
            <Input
              label="Zip/Postal Code *"
              value={form.zipCode}
              onChange={update("zipCode")}
              disabled={!editMode}
              error={errors.zipCode}
            />
          </div>
          <Select
            label="Company Industry *"
            value={form.industryId}
            onChange={updateInt("industryId")}
            options={industryOptions}
            disabled={!editMode}
            error={errors.industryId}
          />
        </section>

        {/* ── Additional Information ── */}
        <section className="ucj-form-section">
          <h3 className="ucj-section-heading">Additional Information</h3>
          <div className="ucj-form-row">
            <Select
              label="Claim Entitlement as"
              value={form.claimingEntitlementAs}
              onChange={updateInt("claimingEntitlementAs")}
              options={claimOptions}
              disabled={!editMode}
            />
            <Input
              label="Partner Referral"
              value={form.partnerReferral}
              onChange={update("partnerReferral")}
              disabled={!editMode}
              placeholder="Enter partner referral"
            />
          </div>
        </section>

        {/* ── Terms ── */}
        {editMode && (
          <div className="ucj-terms">
            <Checkbox
              id="agreeTerms"
              checked={form.agreeToRegisterTerms}
              onChange={(e) => dispatch({ type: "UPDATE_FORM", payload: { agreeToRegisterTerms: e.target.checked } })}
              label={
                <span>
                  By clicking Register you agree to{" "}
                  <a href="https://www.nvidia.com/en-us/about-nvidia/privacy-policy/" target="_blank" rel="noreferrer">NVIDIA Account Terms and Conditions</a>,{" "}
                  <a href="https://www.nvidia.com/en-us/about-nvidia/privacy-policy/" target="_blank" rel="noreferrer">DGX Terms of Service</a>, &amp;{" "}
                  <a href="https://www.nvidia.com/en-us/about-nvidia/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>
                </span>
              }
            />
            {errors.agreeToRegisterTerms && (
              <span className="ucj-error">{errors.agreeToRegisterTerms}</span>
            )}
          </div>
        )}

        {/* ── Action bar ── */}
        <div className="ucj-action-bar">
          {state.steps.length > 1 && (
            <button className="ucj-btn ucj-btn--secondary" onClick={handleBack}>
              Back
            </button>
          )}
          {isReturning && !editMode && (
            <button
              className="ucj-btn ucj-btn--secondary"
              onClick={() => setEditMode(true)}
            >
              Edit
            </button>
          )}
          <button className="ucj-btn ucj-btn--primary" onClick={handleContinue}>
            {editMode && !isReturning ? "Continue" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
